import React, { useState, useMemo, useCallback } from 'react';
import { OutlineItem } from '../wizard/useWizardStore';
import {
  MagnifyingGlassIcon, ChevronDownIcon, ChevronRightIcon,
  PencilIcon, TrashIcon, PlusIcon, CheckIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

interface OutlineSidebarProps {
  items: OutlineItem[];
  activeId: string | null;
  onItemClick: (uid: string) => void;
  onOutlineChange?: (items: OutlineItem[]) => void;
}

let _uidCounter = 0;
function genUid(): string {
  _uidCounter += 1;
  return `new-${Date.now().toString(36)}-${_uidCounter}`;
}

/** Recursively renumber the entire outline tree */
function renumberTree(items: OutlineItem[], parentPath: number[] = []): OutlineItem[] {
  return items.map((item, index) => {
    const num = index + 1;
    const path = [...parentPath, num];
    const newId = path.join('.');
    return {
      ...item,
      id: newId,
      children: item.children && item.children.length > 0
        ? renumberTree(item.children, path)
        : item.children,
    };
  });
}

/** Parse a chapter id like "3" or "2.1" into numeric parts */
function parseId(id: string): { parts: number[]; num: number } {
  const parts = id.split('.').map(s => parseInt(s)).filter(n => !isNaN(n));
  return { parts, num: parts.length > 0 ? parts[0] : 0 };
}

/** Insert a new item into a list at the specified numeric position, then renumber the tree */
function insertAndRenumber(
  list: OutlineItem[],
  newItem: OutlineItem,
  targetId: string,
  parentPath: number[] = []
): OutlineItem[] {
  const { num: targetNum } = parseId(targetId);

  if (targetNum <= 0) {
    // No valid number: append to end
    const result = [...list, { ...newItem, id: '' }];
    return renumberTree(result, parentPath);
  }

  // Find insertion point based on target number
  let insertIdx = list.length;
  for (let i = 0; i < list.length; i++) {
    const { num } = parseId(list[i].id);
    if (num >= targetNum) {
      insertIdx = i;
      break;
    }
  }

  // Insert with a temporary id, then renumber everything
  const result = [
    ...list.slice(0, insertIdx),
    { ...newItem, id: '' },
    ...list.slice(insertIdx),
  ];
  return renumberTree(result, parentPath);
}

/** Find an item in the tree by uid, return its parent uid and the item path */
function findItemInfo(
  items: OutlineItem[],
  uid: string,
  parentUid: string | null = null
): { parentUid: string | null; siblings: OutlineItem[]; index: number } | null {
  for (let i = 0; i < items.length; i++) {
    if (items[i].uid === uid) {
      return { parentUid, siblings: items, index: i };
    }
    if (items[i].children?.length) {
      const found = findItemInfo(items[i].children!, uid, items[i].uid);
      if (found) return found;
    }
  }
  return null;
}

/** Update item in tree and renumber the affected level */
function updateAndRenumber(
  items: OutlineItem[],
  uid: string,
  newId: string,
  newTitle: string
): OutlineItem[] {
  const info = findItemInfo(items, uid);
  if (!info) return items;

  const { parentUid, siblings, index } = info;
  const updatedItem: OutlineItem = {
    ...siblings[index],
    id: newId,
    title: newTitle,
  };

  // Replace in siblings
  const newSiblings = [
    ...siblings.slice(0, index),
    updatedItem,
    ...siblings.slice(index + 1),
  ];

  // Now we need to update the tree and renumber from root
  const replaceSiblings = (
    list: OutlineItem[],
    targetParentUid: string | null
  ): OutlineItem[] => {
    if (targetParentUid === null) return newSiblings;
    return list.map(item => {
      if (item.uid === targetParentUid) {
        return { ...item, children: renumberTree(newSiblings, parseId(item.id).parts) };
      }
      if (item.children?.length) {
        return { ...item, children: replaceSiblings(item.children, targetParentUid) };
      }
      return item;
    });
  };

  // Renumber the whole tree from root for consistency
  return renumberTree(replaceSiblings(items, parentUid));
}

/** Delete item and renumber */
function deleteAndRenumber(items: OutlineItem[], uid: string): OutlineItem[] {
  const info = findItemInfo(items, uid);
  if (!info) return items;

  const { parentUid, siblings, index } = info;
  const newSiblings = [...siblings.slice(0, index), ...siblings.slice(index + 1)];

  const replaceSiblings = (
    list: OutlineItem[],
    targetParentUid: string | null
  ): OutlineItem[] => {
    if (targetParentUid === null) return newSiblings;
    return list.map(item => {
      if (item.uid === targetParentUid) {
        return { ...item, children: newSiblings.length > 0 ? renumberTree(newSiblings, parseId(item.id).parts) : [] };
      }
      if (item.children?.length) {
        return { ...item, children: replaceSiblings(item.children, targetParentUid) };
      }
      return item;
    });
  };

  return renumberTree(replaceSiblings(items, parentUid));
}

/** Collect sibling IDs to find max number for auto-numbering */
function collectSiblingIds(items: OutlineItem[], parentUid: string | null): { ids: string[]; maxNum: number } {
  if (parentUid === null) {
    const nums = items.map(i => parseId(i.id).num);
    return { ids: items.map(i => i.id), maxNum: nums.length > 0 ? Math.max(...nums) : 0 };
  }
  for (const item of items) {
    if (item.uid === parentUid) {
      const children = item.children || [];
      const nums = children.map(c => {
        const p = parseId(c.id).parts;
        return p.length >= 2 ? p[p.length - 1] : (p.length > 0 ? p[0] : 0);
      });
      return { ids: children.map(c => c.id), maxNum: nums.length > 0 ? Math.max(...nums) : 0 };
    }
    if (item.children?.length) {
      const result = collectSiblingIds(item.children, parentUid);
      if (result.ids.length > 0 || result.maxNum > 0) return result;
    }
  }
  return { ids: [], maxNum: 0 };
}

const OutlineSidebar: React.FC<OutlineSidebarProps> = ({ items, activeId, onItemClick, onOutlineChange }) => {
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editId, setEditId] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [addingToParent, setAddingToParent] = useState<string | null>(null);
  const [newItemId, setNewItemId] = useState('');
  const [newItemTitle, setNewItemTitle] = useState('');

  const toggleExpand = (uid: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  React.useEffect(() => {
    const rootIds = new Set(items.map((i) => i.uid));
    setExpandedIds(rootIds);
  }, [items]);

  const updateItem = useCallback((list: OutlineItem[], uid: string, fn: (item: OutlineItem) => OutlineItem): OutlineItem[] => {
    return list.map((item) => {
      if (item.uid === uid) return fn(item);
      if (item.children?.length) {
        return { ...item, children: updateItem(item.children, uid, fn) };
      }
      return item;
    });
  }, []);

  const removeItem = useCallback((list: OutlineItem[], uid: string): OutlineItem[] => {
    return list
      .filter((item) => item.uid !== uid)
      .map((item) => ({
        ...item,
        children: item.children ? removeItem(item.children, uid) : undefined,
      }));
  }, []);

  const startEdit = (item: OutlineItem) => {
    setEditingId(item.uid);
    setEditId(item.id || '');
    setEditTitle(item.title || '');
  };

  const saveEdit = () => {
    if (editingId && onOutlineChange) {
      const newItems = updateAndRenumber(items, editingId, editId.trim(), editTitle.trim());
      onOutlineChange(newItems);
    }
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const deleteItem = (uid: string) => {
    if (onOutlineChange) {
      const newItems = deleteAndRenumber(items, uid);
      onOutlineChange(newItems);
    }
  };

  const startAdd = (parentUid: string | null) => {
    setAddingToParent(parentUid);
    const { maxNum } = collectSiblingIds(items, parentUid);
    setNewItemId(String(maxNum + 1));
    setNewItemTitle('');
  };

  const saveNew = () => {
    const title = newItemTitle.trim();
    if (!title) return;

    const newItem: OutlineItem = {
      uid: genUid(),
      id: newItemId.trim() || '',
      title,
      children: [],
    };

    if (onOutlineChange) {
      let result: OutlineItem[];
      if (addingToParent) {
        // Insert into parent's children and renumber
        result = items.map(item => {
          if (item.uid === addingToParent) {
            const children = item.children || [];
            const parentPath = parseId(item.id).parts;
            const newChildren = insertAndRenumber(children, newItem, newItemId.trim(), parentPath);
            return { ...item, children: newChildren };
          }
          if (item.children?.length) {
            return { ...item, children: addToChild(items, addingToParent, item, newItem) };
          }
          return item;
        });
        // Flatten: the above map handles the first level, but we need recursive
        result = addToParent(items, addingToParent, newItem, newItemId.trim());
      } else {
        result = insertAndRenumber(items, newItem, newItemId.trim());
      }
      onOutlineChange(result);
    }

    setAddingToParent(null);
    setNewItemId('');
    setNewItemTitle('');
  };

  // Helper: recursive add to parent
  const addToParent = (list: OutlineItem[], targetUid: string, newItem: OutlineItem, targetId: string): OutlineItem[] => {
    return list.map(item => {
      if (item.uid === targetUid) {
        const children = item.children || [];
        const parentPath = parseId(item.id).parts;
        return { ...item, children: insertAndRenumber(children, newItem, targetId, parentPath) };
      }
      if (item.children?.length) {
        return { ...item, children: addToParent(item.children, targetUid, newItem, targetId) };
      }
      return item;
    });
  };

  // Used by saveNew for recursive lookup
  const addToChild = (_list: OutlineItem[], targetUid: string, item: OutlineItem, newItem: OutlineItem): OutlineItem[] => {
    if (item.uid === targetUid) {
      const children = item.children || [];
      return [...children, newItem];
    }
    if (item.children?.length) {
      return item.children.map(c => {
        const result = addToChild(item.children!, targetUid, c, newItem);
        return Array.isArray(result) ? result : c;
      }).flat() as OutlineItem[];
    }
    return [];
  };

  const cancelAdd = () => {
    setAddingToParent(null);
  };

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    const filterTree = (list: OutlineItem[]): OutlineItem[] =>
      list
        .filter((item) => {
          const match = item.title.toLowerCase().includes(q);
          if (item.children && item.children.length > 0) {
            const childMatches = filterTree(item.children);
            return match || childMatches.length > 0;
          }
          return match;
        })
        .map((item) => ({
          ...item,
          children: item.children ? filterTree(item.children) : undefined,
        }));
    return filterTree(items);
  }, [items, search]);

  const renderItem = (item: OutlineItem, level: number = 0): React.ReactNode => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedIds.has(item.uid);
    const isActive = activeId === item.uid;
    const isEditing = editingId === item.uid;

    return (
      <div key={item.uid}>
        {isEditing ? (
          <div className="flex items-center gap-1 py-1 px-2" style={{ paddingLeft: `${8 + level * 16}px` }}>
            <span className="w-4 h-4 shrink-0" />
            <input
              className="w-10 text-xs border border-blue-300 rounded px-1 py-0.5"
              value={editId}
              onChange={(e) => setEditId(e.target.value)}
              placeholder="编号"
            />
            <input
              className="flex-1 text-xs border border-blue-300 rounded px-1 py-0.5"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
              autoFocus
            />
            <button onClick={saveEdit} className="p-0.5 text-green-600 hover:bg-green-50 rounded"><CheckIcon className="w-3.5 h-3.5" /></button>
            <button onClick={cancelEdit} className="p-0.5 text-red-500 hover:bg-red-50 rounded"><XMarkIcon className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <div
            onClick={() => {
              onItemClick(item.uid);
              if (hasChildren) toggleExpand(item.uid);
            }}
            className={`group flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer text-sm transition-colors ${
              isActive
                ? 'bg-brand-50 text-brand-700 font-medium'
                : 'text-slate-700 hover:bg-slate-50'
            }`}
            style={{ paddingLeft: `${8 + level * 16}px` }}
          >
            <span className="w-4 h-4 flex items-center justify-center shrink-0">
              {hasChildren ? (
                isExpanded ? <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRightIcon className="w-3.5 h-3.5 text-slate-400" />
              ) : (
                <span className="w-1 h-1 rounded-full bg-slate-300" />
              )}
            </span>
            <span className="truncate flex-1">{item.id} {item.title}</span>

            {onOutlineChange && (
              <span className="hidden group-hover:flex items-center gap-0.5 shrink-0 ml-1">
                <button
                  onClick={(e) => { e.stopPropagation(); startEdit(item); }}
                  className="p-0.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded"
                  title="编辑"
                >
                  <PencilIcon className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); startAdd(item.uid); }}
                  className="p-0.5 text-slate-400 hover:text-green-500 hover:bg-green-50 rounded"
                  title="添加子章节"
                >
                  <PlusIcon className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); if (window.confirm('确定删除「'+item.title+'」？')) deleteItem(item.uid); }}
                  className="p-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                  title="删除"
                >
                  <TrashIcon className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}

        {addingToParent === item.uid && (
          <div className="flex items-center gap-1 py-1 px-2" style={{ paddingLeft: `${8 + (level + 1) * 16}px` }}>
            <span className="w-4 h-4 shrink-0"><span className="w-1 h-1 rounded-full bg-slate-300 block mx-auto mt-1.5" /></span>
            <input
              className="w-10 text-xs border border-green-300 rounded px-1 py-0.5"
              value={newItemId}
              onChange={(e) => setNewItemId(e.target.value)}
              placeholder="编号"
            />
            <input
              className="flex-1 text-xs border border-green-300 rounded px-1 py-0.5"
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveNew(); if (e.key === 'Escape') cancelAdd(); }}
              placeholder="子章节标题"
              autoFocus
            />
            <button onClick={saveNew} className="p-0.5 text-green-600 hover:bg-green-50 rounded"><CheckIcon className="w-3.5 h-3.5" /></button>
            <button onClick={cancelAdd} className="p-0.5 text-red-500 hover:bg-red-50 rounded"><XMarkIcon className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {hasChildren && isExpanded && (
          <div>{item.children!.map((child) => renderItem(child, level + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full bg-white border-r border-slate-200 flex flex-col">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">📑 目录导航</h3>
        {onOutlineChange && (
          <button
            onClick={() => startAdd(null)}
            className="p-1 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded"
            title="添加章节"
          >
            <PlusIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {addingToParent === null && (
        <div className="flex items-center gap-1 py-1.5 px-4 border-b border-green-100 bg-green-50/50">
          <input
            className="w-10 text-xs border border-green-300 rounded px-1 py-0.5"
            value={newItemId}
            onChange={(e) => setNewItemId(e.target.value)}
            placeholder="编号"
          />
          <input
            className="flex-1 text-xs border border-green-300 rounded px-1 py-0.5"
            value={newItemTitle}
            onChange={(e) => setNewItemTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveNew(); if (e.key === 'Escape') cancelAdd(); }}
            placeholder="章节标题"
            autoFocus
          />
          <button onClick={saveNew} className="p-0.5 text-green-600 hover:bg-green-50 rounded"><CheckIcon className="w-3.5 h-3.5" /></button>
          <button onClick={cancelAdd} className="p-0.5 text-red-500 hover:bg-red-50 rounded"><XMarkIcon className="w-3.5 h-3.5" /></button>
        </div>
      )}

      <div className="px-3 py-2">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="搜索章节..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {filteredItems.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8">无匹配章节</p>
        ) : (
          filteredItems.map((item) => renderItem(item))
        )}
      </div>
    </div>
  );
};

export default OutlineSidebar;
